import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fromEvent, tap } from 'rxjs';
import type { IConfig, IToggle } from 'unleash-proxy-client';

import { useFlag, useSubscriptions } from '@/hook';

import type { UnleashContextValue } from '../type';
import { createUnleashClient } from '../util';
import UnleashContext from './context';

const UnleashContextProvider = ({
  children,
  config,
  fallbackToggleList,
}: React.PropsWithChildren<{
  config: IConfig;
  fallbackToggleList?: IToggle[];
}>) => {
  const [flagList, setFlagList] = useState([] as string[]);
  const {
    value: isReady,
    setFalse: didStop,
    setTrue: didReady,
  } = useFlag(false);
  const { value: isTouch, setFalse: handsOff, setTrue: touch } = useFlag(false);
  const unleash = useRef(createUnleashClient(config, fallbackToggleList));

  const changeConfig = (config: Partial<IConfig>) => {
    stopUnleash();

    unleash.current = unleash.current.changeConfig(config);

    startUnleash(true).then(touch).then(didReady);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getVariant = useCallback(
    unleash.current.getVariant.bind(unleash.current),
    [flagList],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getVariantPayloadValue = useCallback(
    unleash.current.getVariantPayloadValue.bind(unleash.current),
    [flagList],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isEnabled = useCallback(
    unleash.current.isEnabled.bind(unleash.current),
    [flagList],
  );

  const startUnleash = (force?: boolean) => {
    if (force || !isReady) return unleash.current.start();

    /* v8 ignore next -- @preserve */
    return Promise.resolve();
  };

  const stopUnleash = () => {
    didStop();
    handsOff();

    unleash.current.stop();
  };

  const updateContext = unleash.current.updateContext.bind(unleash.current);

  const contextValue = useMemo(
    () => ({
      flagList,
      isReady,
      changeConfig,
      getVariant,
      getVariantPayloadValue,
      isEnabled,
      startUnleash,
      stopUnleash,
      updateContext,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [flagList, isReady, getVariant, getVariantPayloadValue, isEnabled],
  );

  useSubscriptions(
    () =>
      isReady
        ? [
            fromEvent(unleash.current.client, 'update')
              .pipe(
                /* v8 ignore next -- @preserve */
                tap(() =>
                  /* v8 ignore next -- @preserve */
                  setFlagList(
                    /* v8 ignore next -- @preserve */
                    unleash.current.getFlagList(),
                  ),
                ),
              )
              .subscribe(),
          ]
        : [],
    [isReady],
  );

  useEffect(() => {
    stopUnleash();

    startUnleash().then(touch).then(didReady);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setFlagList(isReady ? unleash.current.getFlagList() : []);
  }, [isReady]);

  useEffect(() => {
    setFlagList(unleash.current.getFlagList());
  }, []);

  return (
    <UnleashContext.Provider value={contextValue as UnleashContextValue}>
      {isTouch ? children : null}
    </UnleashContext.Provider>
  );
};

export default UnleashContextProvider;
